import dayjs from 'dayjs';
import isEmpty from 'lodash/isEmpty';
import React, { ReactNode, useContext, useEffect, useMemo, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useLocation } from 'react-router';

import { FormBlock, StepProps } from '@/widgets/ServiceFormTemplate';

import { UniversalDocumentFields } from '@/features/ui/ProfileChapters';
import { universalDocumentFieldsPreview } from '@/features/ui/ProfileChapters/ui/UniversalDocumentFieldsPreview';
import { usePersonalAndPassportDefaults } from '@/features/ui/Steps/PersonalAndPassportData/hooks/usePersonalAndPassportDefaults';

import {
  ConditionView,
  FormDatePicker,
  FormSelect,
  FormTextInput,
  Grid,
  Loader,
  StepControlPanel,
  useModal,
} from '@/shared/ui';
import { FormCheckbox } from '@/shared/ui/Checkbox/FormCheckbox';
import { FormMultipleInputPhone } from '@/shared/ui/InputPhone/FormMultipleInputPhone';

import {
  allowedToChooseOthersPassportDocumentTypeCodes,
  allowedToChooseUkrainePassportDocumentTypeCodes,
  genders,
  IdConstants,
  YesNoSelectOptions,
} from '@/components/Forms/constants';
import { profileDocumentDataInfoFieldsMapper } from '@/components/Forms/DocumentsData';

import { useERZDocumentTypes } from '@/modules/erz/hooks/useERZDocumentTypes';
import { ExtractFormType } from '@/modules/iac/pages/services/ExtendedCriminalRecord/lib/constants';
import { NameChangedQuestion } from '@/modules/iac/pages/services/ExtendedCriminalRecord/ui/nameChangedQuestion/NameChangedQuestion';

import { ROUTES } from '@/router/baseRoutes';

import { formatDate, getDateWithoutTime } from '@/helpers/dates';

import AuthContext from '@/contexts/Auth/AuthContext';

import { NamesBeforeFieldName, UNDER_LEGAL_AGE } from '@/lib/consts';

import { FormDocument } from '@/api/types';

import { ExtendedCriminalRecordExtractSchema } from '../../model/types';
import { AgeWarningModal } from '../ageWarningModal/AgeWarningModal';

export interface PersonalDataStepProps {
  onExtractFormTypeSelect: (extractFormType: ExtractFormType, isBackToStepZero: boolean) => void;
  onDraftDelete: (request_id: string) => Promise<void>;
}

export const PersonalDataStep: React.FC<StepProps<PersonalDataStepProps>> = ({
  onExtractFormTypeSelect,
  next,
  prev,
  currentRequestId,
  onDraftDelete,
}) => {
  const { control, watch, setValue } = useFormContext<ExtendedCriminalRecordExtractSchema>();
  const { userData, documentsData } = useContext(AuthContext);
  const [
    extractFormType,
    withoutCitizenship,
    lastName,
    firstName,
    middleName,
    dateOfBirth,
    personPassportDocument,
    militaryViolation,
    citizenship,
  ] = useWatch({
    control,
    name: [
      'extractFormType',
      'withoutCitizenship',
      'lastName',
      'firstName',
      'middleName',
      'dateOfBirth',
      'personPassportDocument',
      'militaryViolation',
      'citizenship',
    ],
  });

  const { isLoading, optionsCountry, availableGenders } = usePersonalAndPassportDefaults();
  const todayDate = dayjs(getDateWithoutTime(new Date()));
  const legalAgeDate = dayjs(getDateWithoutTime(new Date())).subtract(UNDER_LEGAL_AGE, 'years');
  const { isOpen: isAgeModalOpen, openModal: openAgeModal, closeModal: closeAgeModal } = useModal();
  const { state: locationState } = useLocation();
  const goBackRef = useRef(locationState?.from ?? ROUTES.INTERNAL_SERVICES);
  const { data: allowedPersonDocTypes = [] } = useERZDocumentTypes({
    allowedDocumentTypeCodes:
      citizenship?.id === IdConstants.UKRAINE
        ? allowedToChooseUkrainePassportDocumentTypeCodes
        : allowedToChooseOthersPassportDocumentTypeCodes,
  });

  useEffect(() => {
    const isDocumentFilledByUser = !isEmpty(personPassportDocument);

    if (!allowedPersonDocTypes?.length || isDocumentFilledByUser) return;

    const { passport } = documentsData;
    if (!passport?.type) return;

    const documentType = allowedPersonDocTypes.find(({ code }) => code === passport?.type);
    if (!documentType) return;

    // @ts-ignore
    setValue('personPassportDocument', documentType);
    setValue(`documents.${documentType?.code}.info`, profileDocumentDataInfoFieldsMapper(passport?.fields));
    setValue(`documents.${documentType?.code}.payloadInfo`, passport?.fields);
  }, [personPassportDocument, allowedPersonDocTypes, documentsData, setValue]);

  return (
    <ConditionView if={!isLoading} else={<Loader centered />}>
      <FormBlock title="Ваші особисті дані">
        <FormTextInput
          name="lastName"
          control={control}
          label="Прізвище"
          placeholder="Прізвище"
          required
          disabled={!!lastName}
        />
        <FormTextInput
          name="firstName"
          control={control}
          label="Імʼя"
          placeholder="Імʼя"
          required
          disabled={!!firstName}
        />
        <FormTextInput
          name="middleName"
          control={control}
          label="По батькові"
          placeholder="Введіть по батькові"
          disabled={!!middleName}
        />
        <FormDatePicker
          name="dateOfBirth"
          control={control}
          maxDate={userData?.date_of_birth ? legalAgeDate : todayDate}
          required
          label="Дата народження"
        />
        <FormSelect
          name="sex"
          control={control}
          options={availableGenders}
          required
          label="Стать"
          useValueForOnChange
        />
        <FormSelect
          name="withoutCitizenship"
          control={control}
          options={Object.values(YesNoSelectOptions)}
          required
          label="Особа без громадянства"
          disabled={userData?.citizenship?.id === IdConstants.UKRAINE}
        />
        <FormSelect
          name="citizenship"
          label="Громадянство"
          control={control}
          options={optionsCountry}
          required={withoutCitizenship?.id === YesNoSelectOptions.NO.id}
          onChange={() => {
            // @ts-ignore
            setValue('personPassportDocument', undefined);
            setValue(`documents`, []);
          }}
        />
        <FormTextInput name="email" placeholder="example@example.com" control={control} label="Email" required />
        <FormMultipleInputPhone
          name="phones"
          control={control}
          label="Номер телефона"
          required
          maxPhoneNumbersQuantity={2}
        />
        <ConditionView if={extractFormType === ExtractFormType.ELECTRONIC}>
          <FormTextInput
            name="rnokpp"
            control={control}
            label="Ідентифікаційний код (РНОКПП)"
            placeholder="Введіть номер"
            defaultValue={userData?.drfo_code}
            required
            disabled
          />
        </ConditionView>
      </FormBlock>

      <NameChangedQuestion />

      <ConditionView if={extractFormType === ExtractFormType.PAPER}>
        <FormBlock title="Ваші паспортні дані">
          <UniversalDocumentFields
            key="documentFields"
            fieldName="personPassportDocument"
            documentTypeEntities={allowedPersonDocTypes}
            documentTypeLabel="Тип документа, що посвідчує особу"
            layout="service"
            documentRequired
            control={control}
            watch={watch}
          />
          <FormTextInput
            name="rnokpp"
            control={control}
            label="Ідентифікаційний код (РНОКПП)"
            placeholder="Введіть номер"
            defaultValue={userData?.drfo_code}
            required
            disabled
          />
        </FormBlock>
        <FormBlock title="Інформація про військовий облік">
          <FormCheckbox
            name="militaryViolation"
            control={control}
            defaultValue={undefined}
            label="Інформація про порушення правил військового обліку відсутня"
          />
        </FormBlock>
      </ConditionView>

      <StepControlPanel
        next={() => {
          const legalAge = new Date();
          legalAge.setFullYear(new Date().getFullYear() - UNDER_LEGAL_AGE);
          legalAge.setHours(0, 0, 0, 0);

          if (new Date(dateOfBirth) > legalAge) {
            openAgeModal();
            return;
          }

          next();
        }}
        disabledNext={extractFormType === ExtractFormType.PAPER && !militaryViolation}
        prev={() => {
          onExtractFormTypeSelect(ExtractFormType.UNSET, true);
          prev();
        }}
      />
      <AgeWarningModal
        isModalOpen={isAgeModalOpen}
        closeModal={closeAgeModal}
        deleteDraft={onDraftDelete}
        requestId={currentRequestId!}
        goBackRef={goBackRef}
      />
    </ConditionView>
  );
};

type NamesBeforeValues = Partial<
  Record<
    NamesBeforeFieldName.previousLastName | NamesBeforeFieldName.previousName | NamesBeforeFieldName.previousPatronymic,
    string
  >
>;

const getNamesBeforeValue = (namesBefore: Nullable<NamesBeforeValues[]>): ReactNode => {
  if (!namesBefore?.length) {
    return null;
  }

  const values = namesBefore.map((nameBefore) =>
    [
      nameBefore[NamesBeforeFieldName.previousLastName],
      nameBefore[NamesBeforeFieldName.previousName],
      nameBefore[NamesBeforeFieldName.previousPatronymic],
    ]
      .filter(Boolean)
      .map((value) => <div key={value}>{value}</div>),
  );

  return (
    <div>
      {values.map((value, index) => (
        <>
          {index !== 0 && <br />}
          <div key={crypto.randomUUID()}>{value}</div>
        </>
      ))}
    </div>
  );
};

export const PersonalDataPreview: React.FC = () => {
  const { getValues } = useFormContext<ExtendedCriminalRecordExtractSchema>();
  const {
    extractFormType,
    lastName,
    firstName,
    middleName,
    dateOfBirth,
    sex,
    withoutCitizenship,
    citizenship,
    email,
    phones,
    isNameChanged,
    namesBefore,
    rnokpp,
    personPassportDocument,
    documents,
    militaryViolation,
  } = getValues();

  const genderLabel = genders.find((option) => option.id === sex)?.label ?? null;
  const phonesValue = phones?.join(', ') ?? null;
  const namesBeforeValue = useMemo(
    () => getNamesBeforeValue(namesBefore as Nullable<NamesBeforeValues[]>),
    [namesBefore],
  );

  const personalDataRows: Array<[string, Nullable<string>]> = [
    ['Прізвище', lastName],
    ['Ім’я', firstName],
    ['По батькові', middleName],
    ['Дата народження', formatDate(dateOfBirth)],
    ['Стать', genderLabel],
    ['Особа без громадянства', withoutCitizenship?.label ?? null],
    ['Громадянство', citizenship?.label ?? null],
    ['Email', email],
    ['Номер телефона', phonesValue],
  ];

  if (extractFormType === ExtractFormType.ELECTRONIC) {
    personalDataRows.push(['Ідентифікаційний код (РНОКПП)', rnokpp]);
  }

  const documentCode = (personPassportDocument as Nullable<{ code?: string }>)?.code;
  const documentsByCode = documents as Nullable<Record<string, { info?: Record<string, unknown> }>>;
  const passportDocument: FormDocument = {
    type: personPassportDocument as FormDocument['type'],
    info: documentCode ? documentsByCode?.[documentCode]?.info : undefined,
  };
  const passportDataRows = universalDocumentFieldsPreview({
    documentTypeLabel: 'Тип документа, що посвідчує особу',
    document: passportDocument,
  });

  passportDataRows.push(['Ідентифікаційний код (РНОКПП)', rnokpp]);
  passportDataRows.push([
    'Інформація про порушення правил військового обліку',
    militaryViolation ? 'Відсутня' : 'Наявна',
  ]);

  return (
    <>
      <Grid title="Ваші особисті дані" data={personalDataRows} />
      <Grid
        title="Зміна прізвища, ім’я та по батькові"
        data={[
          ['Чи була зміна прізвища, ім’я або по батькові?', isNameChanged?.label ?? null],
          // @ts-ignore
          ...(namesBefore?.length ? [['Попередні прізвище, ім’я та по батькові', namesBeforeValue]] : []),
        ]}
      />
      <ConditionView if={extractFormType === ExtractFormType.PAPER}>
        <Grid title="Ваші паспортні дані" data={passportDataRows} />
      </ConditionView>
    </>
  );
};
